import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  CheckCircle,
  XCircle,
  MapPin,
  Calendar,
  Users,
  Package,
  FileCheck,
  Download,
  Loader2,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Shield,
  User,
  HardDrive,
  Clock,
  RefreshCw,
  Info
} from 'lucide-react';
import { toast } from 'react-toastify';
import { organizeEquipmentHierarchy, groupEquipmentByBaseName } from '../../project/utils/equipmentUtils';
import {
  ProjectApprovalDetails,
  getProjectApprovalDetails,
  AssetApprovalDetail,
  CheckpointApprovalDetail,
  EquipmentApprovalDetail,
  ResourceApprovalDetail,
  approveProjectCheckpoint,
  rejectProjectCheckpoint,
  approveEquipment,
  rejectEquipment,
  approveResourceAssignment,
  rejectResourceAssignment,
  approveProjectLevel,
  rejectProjectLevel,
  approveBulkCheckpoints,
  rejectBulkCheckpoints,
  approveProjectAsset,
  rejectProjectAsset,
  approveAllAndSubmit,
  resubmitProject
} from '../../../services/projectApprovalService';
import { assignmentService } from '../../../services/assignmentService';
import { equipmentService } from '../../../services/equipmentService';
import taskService from '../../../services/taskService';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

import { Button } from 'primereact/button';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import {
  SectionHeader,
  KpiCard,
  HierarchyRail,
  JourneyStep,
  ApproverTile,
  EmptyApprover,
  InfoTile,
  getStatusBadge,
  ChangeComparison,
  CheckpointRow,
  ActionModal,
  OverviewTab,
  AssetsTab,
  ResourcesTab,
  RejectionSummary,
  BRAND_COLORS,
  getCheckpointDisplayGroup
} from './ApprovalComponents';

interface ProjectApprovalDrawerProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onApprovalComplete?: () => void;
}

const ProjectApprovalDrawer: React.FC<ProjectApprovalDrawerProps> = ({
  open,
  onClose,
  projectId,
  onApprovalComplete,
}) => {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const isAdmin = (role || user?.role?.name || '').toLowerCase().includes('admin');
  // Check if user is Head (approver role). Manager/requester must NOT see approve/reject.
  const roleName = (role || user?.role?.name || '').toLowerCase();
  const roleType = (user?.role?.roleType || '').toLowerCase();
  const isHead = (roleName.includes('head') || isAdmin) && roleType !== 'requester';
  const [showRejectionSummary, setShowRejectionSummary] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);
  const [showRejectProjectModal, setShowRejectProjectModal] = useState(false);
  const [projectRejectionReason, setProjectRejectionReason] = useState('');
  const [rejectingProject, setRejectingProject] = useState(false);
  const [commentDrawer, setCommentDrawer] = useState<{
    open: boolean;
    type: 'checkpoint' | 'equipment' | 'resource' | 'asset' | 'bulk-resources';
    action: 'approve' | 'reject';
    itemId: string;
    itemName: string;
    itemIds?: string[];
    resourceType?: 'engineer' | 'supervisor';
    supervisorId?: string;
    engineerIds?: string[];
  } | null>(null);
  const [actionComments, setActionComments] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [forceGenLoading, setForceGenLoading] = useState(false);
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  const [showResubmitConfirm, setShowResubmitConfirm] = useState(false);
  const [resubmitComment, setResubmitComment] = useState('');
  const [reviewSubmittedByApprover, setReviewSubmittedByApprover] = useState(false);
  const [approvingAllAndSubmitting, setApprovingAllAndSubmitting] = useState(false);

  // Missing state declarations restored
  const [projectDetails, setProjectDetails] = useState<ProjectApprovalDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedAssets, setExpandedAssets] = useState<Set<string>>(new Set());
  const [expandedEquipment, setExpandedEquipment] = useState<Set<string>>(new Set());
  const [selectedCheckpoints, setSelectedCheckpoints] = useState<Set<string>>(new Set());
  const [actionModal, setActionModal] = useState<{
    open: boolean;
    type: 'checkpoint' | 'equipment' | 'resource' | 'asset';
    itemId: string;
    itemName: string;
    action: 'approve' | 'reject';
  } | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'assets' | 'resources'>('overview');

  useEffect(() => {
    if (open && projectId) {
      loadProjectDetails();
    }
  }, [open, projectId]);

  // Reset review submitted state when project status shifts back to an active review state
  // (pending/pending_approval/resubmitted). This re-enables the submit button after resubmission.
  useEffect(() => {
    if (!projectDetails) return;

    const status = projectDetails.overview.status;
    if (status === 'pending_approval' || status === 'resubmitted' || status === 'pending') {
      setReviewSubmittedByApprover(false);
    }
  }, [projectDetails?.overview.status]);

  // Set active tab based on where rejections exist — show the tab with issues first
  useEffect(() => {
    if (!projectDetails?.rejectedItems) return;

    const assetRejections = projectDetails.rejectedItems.checkpoints?.length || 0;
    const resourceRejections = projectDetails.rejectedItems.resources?.length || 0;

    if (resourceRejections > 0 && assetRejections === 0) {
      setActiveTab('resources');
    } else if (assetRejections > 0 && resourceRejections === 0) {
      setActiveTab('assets');
    } else if (assetRejections > 0 || resourceRejections > 0) {
      setActiveTab(assetRejections >= resourceRejections ? 'assets' : 'resources');
    }
  }, [projectDetails?.rejectedItems]);

  const loadProjectDetails = async (forceRefresh: boolean = false) => {
    try {
      setLoading(true);
      // Always fetch fresh data to sync with approvals from other pages (ProjectAssignments, etc.)
      const details = await getProjectApprovalDetails(projectId);
      console.log('📥 Loaded project details:', {
        supervisors: details.resources.hierarchy?.length || 0,
        engineers: details.resources.independentEngineers?.length || 0,
        resources: details.resources
      });
      setProjectDetails(details);

      // If project is fully approved, notify parent component
      if ((details.overview as any).approvalStatus === 'approved' && onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error: any) {
      console.error('Failed to load project details:', error);
      toast.error(error.response?.data?.message || 'Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const toggleAssetExpand = (assetId: string) => {
    setExpandedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const toggleEquipmentExpand = (equipmentId: string) => {
    setExpandedEquipment(prev => {
      const newSet = new Set(prev);
      if (newSet.has(equipmentId)) {
        newSet.delete(equipmentId);
      } else {
        newSet.add(equipmentId);
      }
      return newSet;
    });
  };

  const handleCheckpointAction = async (
    checkpointId: string,
    checkpointName: string,
    action: 'approve' | 'reject'
  ) => {
    // For rejections, open drawer for comments (required)
    if (action === 'reject') {
      setCommentDrawer({
        open: true,
        type: 'checkpoint',
        action,
        itemId: checkpointId,
        itemName: checkpointName
      });
      return;
    }

    // For approvals, directly approve without confirmation
    try {
      setActionLoading(true);
      await approveProjectCheckpoint(checkpointId, projectId, undefined);
      toast.success('✅ Checkpoint approved successfully');
      await loadProjectDetails();
      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error: any) {
      console.error('Failed to approve checkpoint:', error);
      toast.error(error.response?.data?.message || 'Failed to approve checkpoint');
    } finally {
      setActionLoading(false);
    }
  };

  // Handler for bulk checkpoint actions
  const handleBulkCheckpointAction = async (action: 'approve' | 'reject') => {
    if (selectedCheckpoints.size === 0) {
      toast.warning('Please select at least one checkpoint');
      return;
    }

    const checkpointIds = Array.from(selectedCheckpoints);

    // For rejections, open drawer for comments (required)
    if (action === 'reject') {
      const checkpointNames = checkpointIds.map(id => {
        // Find checkpoint name from project details
        if (!projectDetails) return 'Checkpoint';
        for (const asset of projectDetails.assets) {
          const allCheckpoints = [
            ...asset.checkpoints.masterCheckpoints,
            ...asset.checkpoints.projectLevelCheckpoints
          ];
          const checkpoint = allCheckpoints.find(cp => cp._id === id);
          if (checkpoint) return checkpoint.name;
        }
        return 'Checkpoint';
      });

      setCommentDrawer({
        open: true,
        type: 'checkpoint',
        action,
        itemId: checkpointIds[0], // Keep for backward compatibility
        itemName: checkpointIds.length === 1 ? checkpointNames[0] : `${checkpointIds.length} Checkpoints`,
        itemIds: checkpointIds // For bulk operations
      });
      return;
    }

    // For approvals, directly approve without confirmation
    try {
      setActionLoading(true);
      await approveBulkCheckpoints(checkpointIds, projectId);
      toast.success(`✅ ${checkpointIds.length} checkpoint(s) approved successfully`);
      setSelectedCheckpoints(new Set()); // Clear selections after bulk approval
      await loadProjectDetails();
      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error: any) {
      console.error('Failed to approve checkpoints:', error);
      toast.error(error.response?.data?.message || 'Failed to approve checkpoints');
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle checkpoint selection
  const toggleCheckpointSelection = (checkpointId: string) => {
    setSelectedCheckpoints(prev => {
      const newSet = new Set(prev);
      if (newSet.has(checkpointId)) {
        newSet.delete(checkpointId);
      } else {
        newSet.add(checkpointId);
      }
      return newSet;
    });
  };

  // Select all checkpoints in an asset
  const selectAllCheckpointsInAsset = (asset: AssetApprovalDetail) => {
    // Check if project is resubmitted
    const projectIsResubmitted = !!(projectDetails?.overview.status === 'pending_approval' &&
      (projectDetails?.overview.projectLevelApproverComments || projectDetails?.overview.projectLevelRejectedAt));

    const allCheckpointIds = new Set<string>();
    asset.checkpoints.masterCheckpoints.forEach(cp => {
      // Only select actionable checkpoints (pending, modified, or rejected if resubmitted)
      if (cp.approvalStatus === 'pending' || cp.approvalStatus === 'modified' || (projectIsResubmitted && cp.approvalStatus === 'rejected')) {
        allCheckpointIds.add(cp._id);
      }
    });
    asset.checkpoints.projectLevelCheckpoints.forEach(cp => {
      if (cp.approvalStatus === 'pending' || cp.approvalStatus === 'modified' || (projectIsResubmitted && cp.approvalStatus === 'rejected')) {
        allCheckpointIds.add(cp._id);
      }
    });

    setSelectedCheckpoints(prev => {
      const newSet = new Set(prev);
      allCheckpointIds.forEach(id => newSet.add(id));
      return newSet;
    });
  };

  // Clear all checkpoint selections
  const clearCheckpointSelections = () => {
    setSelectedCheckpoints(new Set());
  };

  // Select all actionable checkpoints in the "Added project level new" group for an asset
  const selectAllCheckpointsInProjectLevelNew = (asset: AssetApprovalDetail) => {
    const projectIsResubmitted = !!(projectDetails?.overview.status === 'pending_approval' &&
      (projectDetails?.overview.projectLevelApproverComments || projectDetails?.overview.projectLevelRejectedAt));
    const allIds = new Set<string>();
    const addActionable = (cp: CheckpointApprovalDetail) => {
      if (getCheckpointDisplayGroup(cp) !== 'newAtProject') return;
      if (cp.approvalStatus === 'pending' || cp.approvalStatus === 'modified' || (projectIsResubmitted && cp.approvalStatus === 'rejected')) {
        allIds.add(cp._id);
      }
    };
    asset.checkpoints.masterCheckpoints.forEach(addActionable);
    asset.checkpoints.projectLevelCheckpoints.forEach(addActionable);
    setSelectedCheckpoints(prev => {
      const next = new Set(prev);
      allIds.forEach(id => next.add(id));
      return next;
    });
  };

  // Select all actionable checkpoints in an asset for a given frequency
  const selectAllCheckpointsInFrequency = (asset: AssetApprovalDetail, frequency: string) => {
    const projectIsResubmitted = !!(projectDetails?.overview.status === 'pending_approval' &&
      (projectDetails?.overview.projectLevelApproverComments || projectDetails?.overview.projectLevelRejectedAt));
    const allIds = new Set<string>();
    const addActionable = (cp: { _id: string; frequency: string; approvalStatus?: string }) => {
      if (cp.frequency !== frequency) return;
      if (cp.approvalStatus === 'pending' || cp.approvalStatus === 'modified' || (projectIsResubmitted && cp.approvalStatus === 'rejected')) {
        allIds.add(cp._id);
      }
    };
    asset.checkpoints.masterCheckpoints.forEach(addActionable);
    asset.checkpoints.projectLevelCheckpoints.forEach(addActionable);
    setSelectedCheckpoints(prev => {
      const next = new Set(prev);
      allIds.forEach(id => next.add(id));
      return next;
    });
  };

  // Select all pending/actionable checkpoints across all assets
  const selectAllPendingCheckpoints = () => {
    const projectIsResubmitted = !!(projectDetails?.overview.status === 'pending_approval' &&
      (projectDetails?.overview.projectLevelApproverComments || projectDetails?.overview.projectLevelRejectedAt));
    const allIds = new Set<string>();
    (projectDetails?.assets ?? []).forEach((asset) => {
      asset.checkpoints.masterCheckpoints.forEach((cp) => {
        if (cp.approvalStatus === 'pending' || cp.approvalStatus === 'modified' || (projectIsResubmitted && cp.approvalStatus === 'rejected')) {
          allIds.add(cp._id);
        }
      });
      asset.checkpoints.projectLevelCheckpoints.forEach((cp) => {
        if (cp.approvalStatus === 'pending' || cp.approvalStatus === 'modified' || (projectIsResubmitted && cp.approvalStatus === 'rejected')) {
          allIds.add(cp._id);
        }
      });
    });
    setSelectedCheckpoints(allIds);
  };

  const handleEquipmentAction = (
    equipmentId: string,
    equipmentName: string,
    action: 'approve' | 'reject'
  ) => {
    setCommentDrawer({
      open: true,
      type: 'equipment',
      action,
      itemId: equipmentId,
      itemName: equipmentName
    });
  };

  // Simple: Get or create assignment for resource approval
  // Uses any equipment from project to create assignment if needed
  const getOrCreateResourceAssignment = async (
    resource: ResourceApprovalDetail,
    resourceType?: 'engineer' | 'supervisor'
  ): Promise<string> => {
    // Step 1: Use existing assignmentId if available
    if (resource.assignmentId) {
      return resource.assignmentId;
    }

    // Step 2: Find existing assignment
    try {
      const response = await assignmentService.getAssignments({
        projectId: projectId,
        assigneeId: resource._id,
        assigneeRole: resourceType === 'supervisor' ? 'Site Supervisor' : 'Site Engineer',
        isActive: true
      });

      let assignments: any[] = [];
      if (Array.isArray(response?.data)) {
        assignments = response.data;
      } else if (response?.data?.assignments && Array.isArray(response.data.assignments)) {
        assignments = response.data.assignments;
      }

      if (assignments.length > 0) {
        const assignmentId = assignments[0]._id || assignments[0].id || assignments[0].assignmentId;
        if (assignmentId) return assignmentId;
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }

    // Step 3: Sync to create assignments for equipment
    try {
      await assignmentService.syncProjectAssignments(projectId);
      await new Promise(resolve => setTimeout(resolve, 500));

      // Retry finding assignment
      const retryResponse = await assignmentService.getAssignments({
        projectId: projectId,
        assigneeId: resource._id,
        assigneeRole: resourceType === 'supervisor' ? 'Site Supervisor' : 'Site Engineer',
        isActive: true
      });

      let retryAssignments: any[] = [];
      if (Array.isArray(retryResponse?.data)) {
        retryAssignments = retryResponse.data;
      } else if (retryResponse?.data?.assignments && Array.isArray(retryResponse.data.assignments)) {
        retryAssignments = retryResponse.data.assignments;
      }

      if (retryAssignments.length > 0) {
        const assignmentId = retryAssignments[0]._id || retryAssignments[0].id || retryAssignments[0].assignmentId;
        if (assignmentId) return assignmentId;
      }
    } catch (syncError) {
      console.error('Sync failed:', syncError);
    }

    // Step 4: Find any equipment in project to create assignment
    // Use resource's equipment first, or any project equipment
    let equipmentId: string | null = null;

    // Try resource's equipment first
    if (resource.equipment && resource.equipment.length > 0) {
      equipmentId = resource.equipment[0]._id;
    } else {
      // Get any equipment from project
      try {
        const projectEquipment = await equipmentService.getByProject(projectId);
        const equipmentList = Array.isArray(projectEquipment) ? projectEquipment : [];
        if (equipmentList.length > 0) {
          equipmentId = equipmentList[0]._id;
        }
      } catch (error) {
        console.error('Error getting project equipment:', error);
      }
    }

    // Step 5: Use sync endpoint to create assignment (simpler, no unnecessary validations)
    if (equipmentId) {
      try {
        // Sync assignments first to ensure assignment exists
        await assignmentService.syncProjectAssignments(projectId);
        await new Promise(resolve => setTimeout(resolve, 500));

        // Retry finding assignment after sync
        const finalResponse = await assignmentService.getAssignments({
          projectId: projectId,
          assigneeId: resource._id,
          assigneeRole: resourceType === 'supervisor' ? 'Site Supervisor' : 'Site Engineer',
          isActive: true
        });

        let finalAssignments: any[] = [];
        if (Array.isArray(finalResponse?.data)) {
          finalAssignments = finalResponse.data;
        } else if (finalResponse?.data?.assignments && Array.isArray(finalResponse.data.assignments)) {
          finalAssignments = finalResponse.data.assignments;
        }

        if (finalAssignments.length > 0) {
          const assignmentId = finalAssignments[0]._id || finalAssignments[0].id || finalAssignments[0].assignmentId;
          if (assignmentId) {
            console.log(`✅ Found assignment after sync for ${resource.name}: ${assignmentId}`);
            return assignmentId;
          }
        }

        // If still no assignment found, throw error
        throw new Error(`Assignment not found for ${resource.name} even after sync. Please ensure equipment is assigned to this resource.`);
      } catch (syncError: any) {
        console.error('Failed to sync/find assignment:', syncError);
        const errorMessage = syncError.response?.data?.message || syncError.message || 'Unknown error';
        throw new Error(`Could not get assignment: ${errorMessage}`);
      }
    }

    // If all fails, throw error
    throw new Error(`Could not get or create assignment for ${resource.name}. Please ensure equipment is assigned to the project.`);
  };

  // Handler for resource approval - Simple: just resource ID and project ID
  const handleResourceApprove = async (
    resource: ResourceApprovalDetail,
    _resourceName: string,
    resourceType?: 'engineer' | 'supervisor',
    _supervisorId?: string
  ) => {
    try {
      // Approve directly without confirmation
      setActionLoading(true);
      await approveResourceAssignment(projectId, resource._id, undefined);
      toast.success(`✅ ${resourceType === 'supervisor' ? 'Supervisor' : 'Engineer'} approved successfully`);

      // Small delay to ensure backend has processed
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadProjectDetails();

      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error: any) {
      console.error('Failed to approve resource:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to approve resource');
    } finally {
      setActionLoading(false);
    }
  };

  // Handler for resource rejection - Simple: just resource ID and project ID
  const handleResourceReject = async (
    resource: ResourceApprovalDetail,
    resourceName: string,
    resourceType?: 'engineer' | 'supervisor',
    supervisorId?: string,
    engineerIds?: string[]
  ) => {
    try {
      // Pass resource._id (user ID) instead of assignmentId - backend expects resourceId
      setCommentDrawer({
        open: true,
        type: 'resource',
        action: 'reject',
        itemId: resource._id, // Use resource ID (user ID) not assignmentId
        itemName: resourceName,
        resourceType,
        supervisorId,
        engineerIds
      });
    } catch (error: any) {
      toast.error(error.message || 'Could not prepare rejection. Please sync assignments and try again.');
    }
  };

  // Handler for syncing missing assignments
  const handleSyncAssignments = async () => {
    try {
      setSyncLoading(true);
      const result = await assignmentService.syncProjectAssignments(projectId);

      if (result.created > 0) {
        toast.success(`✅ Successfully synced ${result.created} assignment records (${result.details.engineers} engineers, ${result.details.supervisors} supervisors)`);
        // Small delay to ensure database is updated
        await new Promise(resolve => setTimeout(resolve, 500));
        // Force reload project details to show updated data with assignments
        await loadProjectDetails();
        // Switch to resources tab if not already there to show the updated approve/reject buttons
        setActiveTab('resources');
      } else if (result.updated > 0) {
        toast.success(`✅ Updated ${result.updated} assignment records`);
        await new Promise(resolve => setTimeout(resolve, 500));
        await loadProjectDetails();
        setActiveTab('resources');
      } else {
        toast.info('ℹ️ No missing assignments found - all assignment records are up to date');
      }
    } catch (error: any) {
      console.error('Sync failed:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to sync assignments');
    } finally {
      setSyncLoading(false);
    }
  };

  // Admin action: Approve everything and force-generate tasks for all assigned resources
  const handleAdminApproveAndGenerate = async () => {
    if (!projectDetails) {
      toast.error('Project details not loaded');
      return;
    }
    try {
      setForceGenLoading(true);
      // 1) Approve project-level
      await approveProjectLevel(projectId);

      // 2) Approve all checkpoints in bulk (master + project-level)
      const allCheckpointIds: string[] = [];
      projectDetails.assets.forEach(asset => {
        asset.checkpoints.masterCheckpoints.forEach(cp => allCheckpointIds.push(cp._id));
        asset.checkpoints.projectLevelCheckpoints.forEach(cp => allCheckpointIds.push(cp._id));
      });
      if (allCheckpointIds.length > 0) {
        // Backend supports bulk approve; for very large sets, chunk to avoid payload bloat
        const chunkSize = 200;
        for (let i = 0; i < allCheckpointIds.length; i += chunkSize) {
          const slice = allCheckpointIds.slice(i, i + chunkSize);
          await approveBulkCheckpoints(slice, projectId);
        }
      }

      // 3) Approve all assets
      for (const asset of projectDetails.assets) {
        await approveProjectAsset(asset._id);
      }

      // 4) Approve all equipment
      for (const asset of projectDetails.assets) {
        for (const eq of asset.equipment) {
          await approveEquipment(eq._id);
        }
      }

      // 5) Approve all resources (supervisors, their engineers, and independent engineers)
      const supervisors = projectDetails.resources.hierarchy || [];
      const independents = projectDetails.resources.independentEngineers || [];
      for (const sup of supervisors) {
        await approveResourceAssignment(projectId, sup._id);
        for (const eng of (sup.subordinates || [])) {
          await approveResourceAssignment(projectId, eng._id);
        }
      }
      for (const eng of independents) {
        await approveResourceAssignment(projectId, eng._id);
      }

      // Allow backend to settle, then reload status
      await new Promise(r => setTimeout(r, 500));
      await loadProjectDetails(true);

      // 6) Generate tasks for all equipment in this project for today
      try {
        const allEquipment = await equipmentService.getByProject(projectId);
        const equipmentIds: string[] = (Array.isArray(allEquipment) ? allEquipment : []).map((e: any) => e._id).filter(Boolean);
        if (equipmentIds.length === 0) {
          toast.info('No equipment found to generate tasks');
        } else {
          const today = new Date();
          const iso = today.toISOString().slice(0, 10);
          await taskService.generateTasksByEquipment({
            equipmentIds,
            startDate: iso,
            endDate: iso,
            projectId,
          });
          toast.success('✅ Tasks force-generated for all assigned equipment');
        }
      } catch (genErr: any) {
        console.error('Task generation failed:', genErr);
        toast.error(genErr?.response?.data?.message || genErr?.message || 'Failed to generate tasks');
      }

      if (onApprovalComplete) onApprovalComplete();
    } catch (err: any) {
      console.error('Admin approve & generate failed:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to approve and generate');
    } finally {
      setForceGenLoading(false);
    }
  };

  const executeAction = async () => {
    if (!actionModal) return;

    const { type, itemId, action } = actionModal;

    // Validate rejection comments - REQUIRED for rejections only
    if (action === 'reject' && !actionComments.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      setActionLoading(true);

      if (type === 'checkpoint') {
        await rejectProjectCheckpoint(itemId, projectId, actionComments);
        toast.error('❌ Checkpoint rejected');
      } else if (type === 'equipment') {
        await rejectEquipment(itemId, actionComments);
        toast.error('❌ Equipment rejected');
      } else if (type === 'resource') {
        if (action === 'approve') {
          await approveResourceAssignment(projectId, itemId, actionComments || undefined);
          toast.success('✅ Resource assignment approved successfully');
        } else {
          await rejectResourceAssignment(projectId, itemId, actionComments);
          toast.error('❌ Resource assignment rejected');
        }
      }

      // Reset modal and reload project details
      setActionModal(null);
      setActionComments('');
      await loadProjectDetails();

      // Notify parent component
      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error: any) {
      console.error('Action failed:', error);
      toast.error(error.response?.data?.message || `Failed to ${action} item`);
    } finally {
      setActionLoading(false);
    }
  };

  // Check if asset has 2+ rejected checkpoints
  const getAssetRejectedCheckpointCount = (asset: AssetApprovalDetail): number => {
    const allCheckpoints = [
      ...asset.checkpoints.masterCheckpoints,
      ...asset.checkpoints.projectLevelCheckpoints
    ];
    return allCheckpoints.filter(cp => cp.approvalStatus === 'rejected').length;
  };

  // Handle asset action (approve/reject)
  const handleAssetAction = (assetId: string, assetName: string, action: 'approve' | 'reject') => {
    setCommentDrawer({
      open: true,
      type: 'asset',
      action,
      itemId: assetId,
      itemName: assetName
    });
  };

  // Execute comment drawer action
  const executeCommentDrawerAction = async () => {
    if (!commentDrawer) return;

    const { type, action, itemId, itemIds } = commentDrawer;

    // Validate rejection comments - REQUIRED for rejections only
    if (action === 'reject' && !actionComments.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      setActionLoading(true);

      if (type === 'asset') {
        if (action === 'approve') {
          await approveProjectAsset(itemId, undefined); // No comments for approve
          toast.success('✅ Asset approved successfully');
        } else {
          await rejectProjectAsset(itemId, actionComments);
          toast.success('❌ Asset rejected');
        }
      } else if (type === 'checkpoint') {
        // Check if this is a bulk operation
        if (itemIds && itemIds.length > 1) {
          // Bulk checkpoint operation
          if (action === 'approve') {
            await approveBulkCheckpoints(itemIds, projectId);
            toast.success(`✅ ${itemIds.length} checkpoint(s) approved successfully`);
          } else {
            await rejectBulkCheckpoints(itemIds, projectId, actionComments);
            toast.success(`❌ ${itemIds.length} checkpoint(s) rejected`);
          }
          // Clear selections after bulk operation
          setSelectedCheckpoints(new Set());
        } else {
          // Single checkpoint operation
          if (action === 'approve') {
            await approveProjectCheckpoint(itemId, projectId, undefined); // No comments for approve
            toast.success('✅ Checkpoint approved successfully');
          } else {
            await rejectProjectCheckpoint(itemId, projectId, actionComments);
            toast.success('❌ Checkpoint rejected');
          }
        }
      } else if (type === 'equipment') {
        if (action === 'approve') {
          await approveEquipment(itemId, undefined); // No comments for approve
          toast.success('✅ Equipment approved successfully');
        } else {
          await rejectEquipment(itemId, actionComments);
          toast.success('❌ Equipment rejected');
        }
      } else if (type === 'resource') {
        // Simple: approve/reject resource directly by resource ID and project ID
        if (!itemId) {
          throw new Error('Resource ID is missing. Please try again.');
        }

        if (action === 'approve') {
          await approveResourceAssignment(projectId, itemId, actionComments || undefined);
          toast.success(`✅ ${commentDrawer.resourceType === 'supervisor' ? 'Supervisor' : 'Engineer'} approved successfully`);
        } else {
          await rejectResourceAssignment(projectId, itemId, actionComments);
          toast.success(`❌ ${commentDrawer.resourceType === 'supervisor' ? 'Supervisor' : 'Engineer'} rejected`);
        }

        // Small delay to ensure backend has processed
        await new Promise(resolve => setTimeout(resolve, 500));
      } else if (type === 'bulk-resources' && itemIds) {
        // Bulk resource operations - Simple: use resource IDs directly
        const promises = itemIds.map(resourceId =>
          action === 'approve'
            ? approveResourceAssignment(projectId, resourceId, undefined) // No comments for approve
            : rejectResourceAssignment(projectId, resourceId, actionComments)
        );
        await Promise.all(promises);
        toast.success(`✅ ${itemIds.length} resource(s) ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      }

      // Reset drawer and reload
      setCommentDrawer(null);
      setActionComments('');

      // Small delay before reloading to ensure backend has processed
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadProjectDetails();

      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error: any) {
      console.error('Action failed:', error);
      toast.error(error.response?.data?.message || `Failed to ${action} item(s)`);
    } finally {
      setActionLoading(false);
    }
  };

  // Calculate review progress - count all reviewable items individually
  const calculateReviewProgress = () => {
    if (!projectDetails) {
      return { percentage: 0, reviewed: 0, total: 0, breakdown: {}, hasRejections: false };
    }

    let reviewed = 0;
    let total = 0;
    const breakdown: Record<string, { reviewed: number; total: number }> = {};
    let hasRejections = false;

    // 1. Count all checkpoints individually (master + project-level)
    let checkpointsReviewed = 0;
    let checkpointsTotal = 0;
    projectDetails.assets.forEach(asset => {
      const allCheckpoints = [
        ...asset.checkpoints.masterCheckpoints,
        ...asset.checkpoints.projectLevelCheckpoints
      ];
      checkpointsTotal += allCheckpoints.length;
      allCheckpoints.forEach(cp => {
        const status = cp.approvalStatus;
        if (status === 'approved' || status === 'rejected') {
          checkpointsReviewed++;
        }
        if (status === 'rejected') {
          hasRejections = true;
        }
      });

      if (asset.approvalStatus === 'rejected') {
        hasRejections = true;
      }
    });
    breakdown.checkpoints = { reviewed: checkpointsReviewed, total: checkpointsTotal };
    reviewed += checkpointsReviewed;
    total += checkpointsTotal;

    // 2. Count all resources individually (supervisors + engineers)
    let resourcesReviewed = 0;
    let resourcesTotal = 0;
    const supervisors = projectDetails.resources.hierarchy || [];
    const independents = projectDetails.resources.independentEngineers || [];

    // Count supervisors
    supervisors.forEach(sup => {
      resourcesTotal++;
      if (sup.approvalStatus === 'approved' || sup.approvalStatus === 'rejected') {
        resourcesReviewed++;
      }
      if (sup.approvalStatus === 'rejected') {
        hasRejections = true;
      }

      // Count their engineers
      (sup.subordinates || []).forEach(eng => {
        resourcesTotal++;
        if (eng.approvalStatus === 'approved' || eng.approvalStatus === 'rejected') {
          resourcesReviewed++;
        }
        if (eng.approvalStatus === 'rejected') {
          hasRejections = true;
        }
      });
    });

    // Count independent engineers
    independents.forEach(eng => {
      resourcesTotal++;
      if (eng.approvalStatus === 'approved' || eng.approvalStatus === 'rejected') {
        resourcesReviewed++;
      }
      if (eng.approvalStatus === 'rejected') {
        hasRejections = true;
      }
    });

    breakdown.resources = { reviewed: resourcesReviewed, total: resourcesTotal };
    reviewed += resourcesReviewed;
    total += resourcesTotal;

    const percentage = total > 0 ? Math.round((reviewed / total) * 100) : 0;
    return { percentage, reviewed, total, breakdown, hasRejections };
  };

  const handleFixIssue = (type: 'checkpoint' | 'resource', _item: any) => {
    onClose();
    if (type === 'resource') {
      navigate(`/projects/${projectId}/assignments`);
    } else {
      navigate(`/projects/view/${projectId}`);
    }
  };

  const handleRejectProject = async () => {
    if (!projectRejectionReason || projectRejectionReason.length < 10) {
      toast.error('Please provide a rejection reason (minimum 10 characters)');
      return;
    }

    try {
      setRejectingProject(true);
      await rejectProjectLevel(projectId, projectRejectionReason);
      toast.success('Project rejected successfully');
      setShowRejectProjectModal(false);
      setProjectRejectionReason('');
      setReviewSubmittedByApprover(true);
      await loadProjectDetails();
      if (onApprovalComplete) onApprovalComplete();
    } catch (error: any) {
      console.error('Failed to reject project:', error);
      toast.error(error.response?.data?.message || 'Failed to reject project');
    } finally {
      setRejectingProject(false);
    }
  };

  // Submit final review
  const handleSubmitReview = async () => {
    setShowSubmitConfirmModal(true);
  };

  const confirmSubmitReview = async () => {
    try {
      setSubmittingReview(true);

      // Check if we have rejections
      const progress = calculateReviewProgress();
      const hasRejections = progress.hasRejections;

      if (hasRejections) {
        // If there are rejections, open the rejection modal instead of directly submitting
        setShowSubmitConfirmModal(false);
        setProjectRejectionReason('');
        setShowRejectProjectModal(true);
      } else {
        // If all approved, approve the project level
        await approveProjectLevel(projectId);
        toast.success('✅ Review submitted (Project Approved)');
        
        setShowSubmitConfirmModal(false);
        setReviewSubmittedByApprover(true); // Disable submit button
        await loadProjectDetails();
  
        if (onApprovalComplete) {
          onApprovalComplete();
        }
      }
    } catch (error: any) {
      console.error('Failed to submit review:', error);
      toast.error(error.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Handle "Approve All & Submit" for Head users
  const handleApproveAllAndSubmit = async () => {
    if (!projectDetails) return;

    try {
      setApprovingAllAndSubmitting(true);
      await approveAllAndSubmit(projectId);
      toast.success('✅ All items approved and project submitted successfully');

      // Reload details and update UI
      await loadProjectDetails();
      setReviewSubmittedByApprover(true);

      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error: any) {
      console.error('Failed to approve all and submit:', error);
      toast.error(error.response?.data?.message || 'Failed to approve all and submit');
    } finally {
      setApprovingAllAndSubmitting(false);
    }
  };

  // Approve project from Overview tab (project-level approval only — does NOT lock Assets/Resources tabs)
  const handleApproveProjectFromOverview = async () => {
    try {
      setSubmittingReview(true);
      await approveProjectLevel(projectId);
      toast.success('✅ Project overview approved — you can still approve/reject assets and assignments');
      // Do NOT set reviewSubmittedByApprover here — overview approval is independent of assets/resources review
      await loadProjectDetails();
      if (onApprovalComplete) onApprovalComplete();
    } catch (error: any) {
      console.error('Failed to approve project:', error);
      toast.error(error.response?.data?.message || 'Failed to approve project');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Check if project is resubmitted (pending approval but has rejection history)
  const isResubmitted = !!(projectDetails?.overview.status === 'pending_approval' &&
    (projectDetails?.overview.projectLevelApproverComments || projectDetails?.overview.projectLevelRejectedAt));

  // Determine if review is submitted (for UI state — locks approve/reject buttons).
  // - Local flag: full approve path or explicit submit success.
  // - projectLevelApprovalStatus === 'rejected': head finalized "Submit review" with rejections
  //   (rejectProjectLevel). Do not use overview.status === 'rejected' — overview.status is
  //   operational (active/inactive) from the API, not approvalStatus.
  // Project-level approval from Overview tab does NOT lock Assets/Resources tabs —
  // those must be approved/rejected independently.
  const isReviewSubmitted = reviewSubmittedByApprover ||
    projectDetails?.overview.projectLevelApprovalStatus === 'rejected';

  // Handle closing the drawer
  const handleClose = () => {
    // Reset local state if needed
    setReviewSubmittedByApprover(false);
    setShowResubmitConfirm(false);
    setResubmitComment('');
    onClose();
  };

  const confirmResubmit = async () => {
    if (resubmitComment.trim().length < 10) {
      toast.error('Please provide a detailed comment (minimum 10 characters)');
      return;
    }
    try {
      setResubmitting(true);
      await resubmitProject(projectId, resubmitComment.trim());
      toast.success('Project resubmitted for review');
      setShowResubmitConfirm(false);
      setResubmitComment('');
      await loadProjectDetails();
      if (onApprovalComplete) onApprovalComplete();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to resubmit project');
    } finally {
      setResubmitting(false);
    }
  };

  return (
    <>
      {/* Confirmation Modal for Submit Review */}
      {showSubmitConfirmModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-blue-100 text-blue-600 rounded-full">
                  <CheckCircle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Submit Review
                  </h3>
                  <p className="text-sm text-gray-500">
                    {projectDetails?.overview.projectName || 'Project'}
                  </p>
                </div>
              </div>

              {/* Message */}
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3">
                  Are you sure you want to submit your review? This action will:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-2">
                  <li>Finalize all your approval/rejection decisions</li>
                  <li>Lock the project from further changes</li>
                  <li>Require the requester to resubmit if any items were rejected</li>
                </ul>
                <p className="text-sm font-semibold text-amber-600 mt-4">
                  ⚠️ This action cannot be undone. Make sure you have reviewed all items.
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowSubmitConfirmModal(false)}
                  disabled={submittingReview}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSubmitReview}
                  disabled={submittingReview}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
                >
                  {submittingReview ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Submit Review
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resubmit Modal */}
      {showResubmitConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-blue-100 text-blue-600 rounded-full">
                  <RefreshCw className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Resubmit for Review</h3>
                  <p className="text-sm text-gray-500">{projectDetails?.overview.projectName || 'Project'}</p>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Comments (required, min 10 chars)</label>
                <textarea
                  value={resubmitComment}
                  onChange={(e) => setResubmitComment(e.target.value)}
                  placeholder="Explain changes made or reason for resubmission..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setShowResubmitConfirm(false); setResubmitComment(''); }}
                  disabled={resubmitting}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmResubmit}
                  disabled={resubmitting || resubmitComment.trim().length < 10}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 font-medium flex items-center gap-2"
                >
                  {resubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <>Resubmit</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={handleClose}
      />

      {/* Drawer — full size */}
      <div className="fixed inset-0 min-h-screen w-full bg-white shadow-2xl z-50 flex flex-col font-[family-name:var(--font-outfit)] text-slate-800">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              type="button"
              onClick={handleClose}
              className="flex items-center gap-1.5 shrink-0 px-2.5 py-1.5 rounded-lg text-slate-600 hover:bg-slate-200/80 hover:text-slate-900 transition-colors border border-slate-200 hover:border-slate-300"
              title="Back to view all approval request projects"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">Back to list</span>
            </button>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-slate-900 truncate">
                Approval Review
              </h2>
              <p className="text-xs text-slate-500 truncate mt-0.5">
                {projectDetails?.overview.projectName || 'Loading...'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              icon="pi pi-refresh"
              rounded
              text
              size="small"
              severity="secondary"
              loading={loading}
              onClick={() => loadProjectDetails(true)}
              title="Refresh"
            />
            <Button
              icon="pi pi-times"
              rounded
              text
              size="small"
              severity="secondary"
              onClick={handleClose}
              aria-label="Close"
            />
          </div>
        </div>

        {/* Progress bar + Review action — only shown to head/approvers */}
        {projectDetails && (
          <div className="px-5 py-3 bg-white border-b border-slate-200">
            {(() => {
              const progress = calculateReviewProgress();
              const isComplete = progress.percentage === 100;
              const hasRejections = progress.hasRejections;
              const allApproved = isComplete && !hasRejections;
              const alreadyDone = isReviewSubmitted;
              const hasRejectedItems =
                (projectDetails.rejectedItems?.checkpoints?.length || 0) > 0 ||
                (projectDetails.rejectedItems?.resources?.length || 0) > 0;
              const projectRejected = projectDetails.overview.status === 'rejected';

              return (
                <div className="flex flex-col gap-2">
                  {/* Top row: progress bar + breakdown labels */}
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Bar */}
                    <div className="flex-1 min-w-[140px] max-w-[280px] flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            alreadyDone ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' :
                            hasRejections && isComplete ? 'bg-gradient-to-r from-red-400 to-red-500' :
                            isComplete ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' :
                            'bg-gradient-to-r from-blue-500 to-blue-600'
                          }`}
                          style={{ width: `${progress.percentage}%` }}
                        />
                      </div>
                      <span className={`text-sm font-bold tabular-nums shrink-0 ${isComplete ? (hasRejections ? 'text-red-600' : 'text-emerald-600') : 'text-blue-600'}`}>
                        {progress.percentage}%
                      </span>
                    </div>

                    {/* Breakdown pills */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {progress.breakdown.checkpoints && progress.breakdown.checkpoints.total > 0 && (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full tabular-nums border ${
                          progress.breakdown.checkpoints.reviewed === progress.breakdown.checkpoints.total
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-slate-100 text-slate-600 border-slate-200'
                        }`}>
                          CP {progress.breakdown.checkpoints.reviewed}/{progress.breakdown.checkpoints.total}
                        </span>
                      )}
                      {progress.breakdown.resources && progress.breakdown.resources.total > 0 && (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full tabular-nums border ${
                          progress.breakdown.resources.reviewed === progress.breakdown.resources.total
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-slate-100 text-slate-600 border-slate-200'
                        }`}>
                          Res {progress.breakdown.resources.reviewed}/{progress.breakdown.resources.total}
                        </span>
                      )}
                      {!isComplete && (
                        <span className="text-[10px] text-slate-500 font-medium">
                          {progress.total - progress.reviewed} remaining
                        </span>
                      )}
                      {isHead && isResubmitted && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold border border-amber-200">
                          <RefreshCw className="w-3 h-3" /> Resubmitted
                        </span>
                      )}
                    </div>

                    {/* Right actions — rejections summary btn */}
                    <div className="flex items-center gap-2 ml-auto shrink-0">
                      {hasRejectedItems && (
                        <Button
                          label="Rejections"
                          icon="pi pi-exclamation-triangle"
                          size="small"
                          severity="danger"
                          outlined
                          onClick={() => setShowRejectionSummary(true)}
                        />
                      )}
                      {/* Requester: resubmit if rejected */}
                      {!isHead && (hasRejectedItems || projectRejected) && (
                        <Button
                          label="Resubmit"
                          icon="pi pi-refresh"
                          size="small"
                          onClick={() => setShowResubmitConfirm(true)}
                        />
                      )}
                    </div>
                  </div>

                  {/* Bottom row: review action button — only shown to head, only when review is complete */}
                  {isHead && !alreadyDone && (
                    <div className={`rounded-xl border px-4 py-3 flex items-center justify-between gap-4 transition-all duration-300 ${
                      isComplete
                        ? allApproved
                          ? 'bg-emerald-50 border-emerald-200'
                          : 'bg-amber-50 border-amber-200'
                        : 'bg-slate-50 border-slate-200 opacity-60'
                    }`}>
                      <div className="min-w-0">
                        {!isComplete && (
                          <>
                            <p className="text-sm font-semibold text-slate-600">Review in progress</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              Approve or reject every checkpoint and assignment before you can submit.
                            </p>
                          </>
                        )}
                        {isComplete && allApproved && (
                          <>
                            <p className="text-sm font-semibold text-emerald-800">All items reviewed &amp; approved</p>
                            <p className="text-xs text-emerald-700 mt-0.5">Click to approve this project and start it.</p>
                          </>
                        )}
                        {isComplete && !allApproved && (
                          <>
                            <p className="text-sm font-semibold text-amber-800">Review complete — some items rejected</p>
                            <p className="text-xs text-amber-700 mt-0.5">Submit your review to notify the requester of rejections.</p>
                          </>
                        )}
                      </div>
                      <div className="shrink-0">
                        {/* Not complete — disabled placeholder */}
                        {!isComplete && (
                          <button
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-300 text-slate-500 text-sm font-semibold cursor-not-allowed select-none"
                          >
                            <Clock className="w-4 h-4" />
                            Review incomplete
                          </button>
                        )}
                        {/* All approved → Approve & Start Project */}
                        {isComplete && allApproved && (
                          <button
                            onClick={handleApproveAllAndSubmit}
                            disabled={approvingAllAndSubmitting}
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow-md shadow-emerald-500/30 disabled:opacity-60 transition-all"
                          >
                            {approvingAllAndSubmitting
                              ? <><Loader2 className="w-4 h-4 animate-spin" /> Approving...</>
                              : <><CheckCircle className="w-4 h-4" /> Approve &amp; Start Project</>
                            }
                          </button>
                        )}
                        {/* Has rejections → Submit Review */}
                        {isComplete && !allApproved && (
                          <button
                            onClick={handleSubmitReview}
                            disabled={submittingReview}
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold shadow-md shadow-amber-500/30 disabled:opacity-60 transition-all"
                          >
                            {submittingReview
                              ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                              : <><XCircle className="w-4 h-4" /> Submit Review</>
                            }
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Done state */}
                  {isHead && alreadyDone && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span className="text-sm font-semibold text-emerald-800">
                        {projectDetails.overview.projectLevelApprovalStatus === 'rejected'
                          ? 'Review submitted — the requester has been notified to address rejected items.'
                          : `Review submitted — project is now ${projectDetails.overview.status}`}
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Tabs — modern pill style with rejection badges and suggestions */}
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
          {(() => {
            const assetRejections = projectDetails
              ? (projectDetails.rejectedItems?.checkpoints?.length || 0)
              : 0;
            const resourceRejections = projectDetails?.rejectedItems?.resources?.length || 0;
            return (
              <div className="space-y-3">
                <div className="inline-flex p-1 rounded-lg bg-slate-200/60 border border-slate-200">
                  <button
                    type="button"
                    onClick={() => setActiveTab('overview')}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      activeTab === 'overview'
                        ? 'bg-white text-blue-600 shadow-sm border border-slate-200'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                    }`}
                  >
                    <Info className="w-4 h-4" />
                    Project Overview
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('assets')}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      activeTab === 'assets'
                        ? 'bg-white text-blue-600 shadow-sm border border-slate-200'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                    } ${assetRejections > 0 ? 'ring-1 ring-amber-400/60' : ''}`}
                  >
                    <Package className="w-4 h-4" />
                    Assets
                    {assetRejections > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded-full">
                        {assetRejections}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('resources')}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      activeTab === 'resources'
                        ? 'bg-white text-blue-600 shadow-sm border border-slate-200'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                    } ${resourceRejections > 0 ? 'ring-1 ring-amber-400/60' : ''}`}
                  >
                    <Users className="w-4 h-4" />
                    Assignments
                    {resourceRejections > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded-full">
                        {resourceRejections}
                      </span>
                    )}
                  </button>
                </div>
                {/* Suggestion banners — shown when tab has rejections (hidden for head/approver; they review, not act on suggestions) */}
                {assetRejections > 0 && activeTab === 'assets' && !isHead && (
                  <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-900">Action needed in Assets</p>
                      <p className="text-xs text-amber-800 mt-0.5">
                        {assetRejections} checkpoint{assetRejections !== 1 ? 's' : ''} need your attention. Review the rejection reasons below, make the necessary changes, and approve or reject each item.
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowRejectionSummary(true)}
                        className="mt-2 text-xs font-semibold text-amber-700 hover:text-amber-900 underline"
                      >
                        View rejection summary →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 text-sm bg-slate-50">
          {loading ? (
            <div className="flex items-center justify-center min-h-[200px]">
              <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
            </div>
          ) : !projectDetails ? (
            <div className="flex items-center justify-center min-h-[200px]">
              <div className="text-center">
                <AlertCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-[11px] text-slate-600">Failed to load project details</p>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && projectDetails && (
                <OverviewTab
                  overview={projectDetails.overview}
                  assets={projectDetails.assets}
                  resources={projectDetails.resources}
                  isHead={isHead}
                  isReviewSubmitted={isReviewSubmitted}
                  isReviewComplete={calculateReviewProgress().percentage === 100}
                  onApproveProject={handleApproveProjectFromOverview}
                  onRejectProject={() => setShowRejectProjectModal(true)}
                  approvingProject={submittingReview}
                />
              )}
              {activeTab === 'assets' && (
                <AssetsTab
                  assets={projectDetails.assets}
                  expandedAssets={expandedAssets}
                  expandedEquipment={expandedEquipment}
                  toggleAssetExpand={toggleAssetExpand}
                  toggleEquipmentExpand={toggleEquipmentExpand}
                  onCheckpointAction={handleCheckpointAction}
                  onEquipmentAction={handleEquipmentAction}
                  onAssetAction={handleAssetAction}
                  getAssetRejectedCheckpointCount={getAssetRejectedCheckpointCount}
                  isReviewSubmitted={isReviewSubmitted || false}
                  isResubmitted={isResubmitted}
                  isHead={isHead}
                  selectedCheckpoints={selectedCheckpoints}
                  toggleCheckpointSelection={toggleCheckpointSelection}
                  selectAllCheckpointsInAsset={selectAllCheckpointsInAsset}
                  selectAllCheckpointsInFrequency={selectAllCheckpointsInFrequency}
                  selectAllCheckpointsInProjectLevelNew={selectAllCheckpointsInProjectLevelNew}
                  selectAllPendingCheckpoints={selectAllPendingCheckpoints}
                  clearCheckpointSelections={clearCheckpointSelections}
                  onBulkCheckpointAction={handleBulkCheckpointAction}
                />
              )}
              {activeTab === 'resources' && (
                <ResourcesTab
                  resources={projectDetails.resources}
                  projectId={projectId}
                  onResourceApprove={handleResourceApprove}
                  onResourceReject={handleResourceReject}
                  actionLoading={actionLoading}
                  onSyncAssignments={handleSyncAssignments}
                  syncLoading={syncLoading}
                  isReviewSubmitted={isReviewSubmitted || false}
                  isResubmitted={isResubmitted}
                  isHead={isHead}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Action Modal - Keep for backward compatibility with resources */}
      {actionModal && actionModal.type === 'resource' && (
        <ActionModal
          open={actionModal.open}
          type={actionModal.type}
          action={actionModal.action}
          itemName={actionModal.itemName}
          comments={actionComments}
          setComments={setActionComments}
          onConfirm={executeAction}
          onCancel={() => {
            setActionModal(null);
            setActionComments('');
          }}
          loading={actionLoading}
        />
      )}

      {/* Comment Drawer for Asset Actions */}
      <RightDrawer
        open={commentDrawer?.open || false}
        onClose={() => {
          setCommentDrawer(null);
          setActionComments('');
        }}
        title={
          commentDrawer
            ? `${commentDrawer.action === 'approve' ? 'Approve' : 'Reject'} ${commentDrawer.itemName}`
            : ''
        }
        size="md"
      >
        <div className="space-y-6">
          <div>
            <p className="text-sm text-gray-600 mb-4">
              {commentDrawer?.action === 'approve' ? (
                <>
                  You are about to approve <strong className="text-green-600">{commentDrawer?.itemName}</strong>
                  {commentDrawer?.resourceType && (
                    <span className="text-gray-500"> ({commentDrawer.resourceType === 'supervisor' ? 'Site Supervisor' : 'Site Engineer'})</span>
                  )}.
                  {(commentDrawer?.type === 'checkpoint' && commentDrawer?.itemIds && commentDrawer.itemIds.length > 1) && (
                    <span className="block mt-2 text-amber-600">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      Please review all selected items before approving.
                    </span>
                  )}
                </>
              ) : (
                <>
                  You are about to reject <strong className="text-red-600">{commentDrawer?.itemName}</strong>
                  {commentDrawer?.resourceType && (
                    <span className="text-gray-500"> ({commentDrawer.resourceType === 'supervisor' ? 'Site Supervisor' : 'Site Engineer'})</span>
                  )}.
                  {commentDrawer?.resourceType === 'supervisor' && commentDrawer?.engineerIds && commentDrawer.engineerIds.length > 0 && (
                    <span className="block mt-2 text-amber-600">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      This supervisor manages {commentDrawer.engineerIds.length} engineer{commentDrawer.engineerIds.length !== 1 ? 's' : ''}. Rejecting the supervisor will affect all managed engineers.
                    </span>
                  )}
                  {commentDrawer?.supervisorId && (
                    <span className="block mt-2 text-blue-600">
                      <Shield className="w-4 h-4 inline mr-1" />
                      This engineer is managed by a supervisor. The supervisor will be notified of this rejection.
                    </span>
                  )}
                  <span className="block mt-2">Please provide a detailed reason for rejection.</span>
                </>
              )}
            </p>
            {/* Only show comment field for rejections */}
            {commentDrawer?.action === 'reject' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  value={actionComments}
                  onChange={(e) => setActionComments(e.target.value)}
                  placeholder="Please provide a detailed reason for rejection..."
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Rejection reason is required
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setCommentDrawer(null);
                setActionComments('');
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={actionLoading}
            >
              Cancel
            </button>
            <button
              onClick={executeCommentDrawerAction}
              disabled={actionLoading || (commentDrawer?.action === 'reject' && !actionComments.trim())}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed ${commentDrawer?.action === 'approve'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'
                }`}
            >
              {actionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : commentDrawer?.action === 'approve' ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Approve {(commentDrawer?.type === 'checkpoint' && commentDrawer?.itemIds && commentDrawer.itemIds.length > 1) ? 'All Selected' : 'Item'}
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  Reject {(commentDrawer?.type === 'checkpoint' && commentDrawer?.itemIds && commentDrawer.itemIds.length > 1) ? 'All Selected' : 'Item'}
                </>
              )}
            </button>
          </div>
        </div>
      </RightDrawer>

      {/* Rejection Summary Drawer */}
      <RightDrawer
        open={showRejectionSummary}
        onClose={() => setShowRejectionSummary(false)}
        title="Rejection Summary"
        size="md"
      >
        {projectDetails && (
          <RejectionSummary
            rejectedItems={projectDetails.rejectedItems}
            onFix={handleFixIssue}
          />
        )}
      </RightDrawer>

      {/* Reject Project Modal */}
      {showRejectProjectModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Reject Project
                </h3>
                <button
                  onClick={() => setShowRejectProjectModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Provide a clear reason for rejection. The project manager will be notified and can address the feedback before resubmitting.
                </p>

                <div className="space-y-2">
                  <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700">
                    Rejection reason (Required) - Minimum 10 characters
                  </label>
                  <textarea
                    id="rejectionReason"
                    rows={4}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-3 border"
                    placeholder="Enter rejection reason..."
                    value={projectRejectionReason}
                    onChange={(e) => setProjectRejectionReason(e.target.value)}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{projectRejectionReason.length >= 10 ? 'Minimum length met' : 'Minimum 10 characters required'}</span>
                    <span>{projectRejectionReason.length}/500</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowRejectProjectModal(false)}
                  disabled={rejectingProject}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectProject}
                  disabled={rejectingProject || projectRejectionReason.length < 10}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {rejectingProject ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Rejecting...
                    </>
                  ) : (
                    <>
                      Reject & Notify Manager
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectApprovalDrawer;
